import { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { StrategistMatrixResponse } from '@/entities/strategist';
import { HeatmapLegend, HeatmapMatrix, RELIABLE_MIN_DIALOGS } from '@/widgets/StrategistHeatmap';
import type { HeatmapCell } from '@/widgets/StrategistHeatmap';
import { MatrixKpis } from './MatrixKpis';
import type { BestPair } from './MatrixKpis';

type Props = {
  /** Данные матрицы (непустые: есть строки и столбцы) */
  data: StrategistMatrixResponse;
  /** Открыть карточку понятия по conceptId (строки-возражения и столбцы-аргументы) */
  onConceptOpen: (conceptId: string) => void;
  /** Проваливание в диалоги ячейки (пара возражение×аргумент) с готовым заголовком */
  onCellOpen: (params: { objectionId: string; argumentId: string; title: string }) => void;
};

/**
 * Визуализация матрицы «возражение × аргумент»: KPI-строка, легенда шкалы и
 * тепловая матрица. Ожидает непустые данные — пустое состояние обрабатывает страница.
 */
export const StrategistMatrixViz: FC<Props> = ({ data, onConceptOpen, onCellOpen }) => {
  const { t } = useTranslation('strategist');

  // Подписи строк/столбцов по id — для заголовка дровера диалогов ячейки.
  const rowLabelById = useMemo(() => new Map(data.rows.map((r) => [r.id, r.label ?? '—'])), [data.rows]);
  const colLabelById = useMemo(() => new Map(data.columns.map((c) => [c.id, c.label ?? '—'])), [data.columns]);

  // Лучшая устойчивая пара: максимальная доля цели среди надёжных ячеек.
  const bestPair = useMemo<BestPair | null>(() => {
    const rowsById = new Map(data.rows.map((r) => [r.id, r.label ?? '—']));
    const columnsById = new Map(data.columns.map((c) => [c.id, c.label ?? '—']));
    let best: BestPair | null = null;
    data.cells.forEach((cell) => {
      if (cell.targetRate === null || cell.nDialogs < RELIABLE_MIN_DIALOGS) return;
      if (best === null || cell.targetRate > best.rate) {
        best = {
          objectionLabel: rowsById.get(cell.objectionId) ?? '—',
          argumentLabel: columnsById.get(cell.argumentId) ?? '—',
          rate: cell.targetRate,
        };
      }
    });
    return best;
  }, [data]);

  // Приводим ячейки матрицы к обобщённой адресации (rowId/colId) тепловой матрицы.
  const heatmapCells = useMemo<HeatmapCell[]>(
    () =>
      data.cells.map((c) => ({
        rowId: c.objectionId,
        colId: c.argumentId,
        nDialogs: c.nDialogs,
        targetRate: c.targetRate,
      })),
    [data.cells],
  );

  return (
    <div className="flex flex-col gap-4">
      <MatrixKpis totalDialogs={data.totalDialogs} baselineRate={data.baselineRate} bestPair={bestPair} />
      <HeatmapLegend
        labels={{
          belowBaseline: t('legend.belowBaseline'),
          nearBaseline: t('legend.nearBaseline'),
          aboveBaseline: t('legend.aboveBaseline'),
          unreliable: (minDialogs) => t('legend.unreliable', { n: minDialogs }),
        }}
      />
      <HeatmapMatrix
        rows={data.rows}
        columns={data.columns}
        cells={heatmapCells}
        baselineRate={data.baselineRate}
        onRowClick={onConceptOpen}
        onColClick={onConceptOpen}
        onCellClick={(rowId, colId) =>
          onCellOpen({
            objectionId: rowId,
            argumentId: colId,
            title: `${rowLabelById.get(rowId) ?? '—'} → ${colLabelById.get(colId) ?? '—'}`,
          })
        }
        labels={{
          corner: t('matrix.corner'),
          total: t('matrix.total'),
          rowName: t('matrix.objection'),
          colName: t('matrix.argument'),
          dialogs: t('matrix.dialogs'),
          targetRate: t('matrix.targetRate'),
          unreliableHint: t('matrix.unreliableHint'),
        }}
      />
    </div>
  );
};
